import CloseIcon from "@mui/icons-material/Close";
import SearchIcon from "@mui/icons-material/Search";
import {
    Autocomplete,
    Box,
    Chip,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    FormControl,
    FormControlLabel,
    IconButton,
    InputAdornment,
    Radio,
    RadioGroup,
    Stack,
    TextField,
    Typography,
} from "@mui/material";
import { DataGrid } from "@mui/x-data-grid";
import { Button } from "@zoom/react-ui";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useSnackbar } from "notistack";
import React, { useMemo, useState } from "react";
import axiosInstance from "@utils/axiosInstance";
import CustomDataGridToolBar from "@components/CustomDataGridToolBar";

const emptyForm = { name: "", email: "", role: "", region: "", userType: "role" };

const ZOOM_EMAIL_REGEX = /^[a-zA-Z0-9._%+-]+@(zoom\.us|zoom\.com)$/i;

const AdminUsers = () => {
    const { enqueueSnackbar } = useSnackbar();
    const queryClient = useQueryClient();
    const [searchInput, setSearchInput] = useState("");
    const [executedQuery, setExecutedQuery] = useState("");
    const [addOpen, setAddOpen] = useState(false);
    const [form, setForm] = useState(emptyForm);
    const [pendingRoleChange, setPendingRoleChange] = useState(null);

    const { data, isFetching } = useQuery({
        queryKey: ["admin-users"],
        queryFn: async () =>
            await axiosInstance.get("/operationmonitor/api/v1/admin/users"),
        refetchOnWindowFocus: false,
    });

    const payload = data?.data?.data || {};
    const users = payload.users || [];
    const roles = payload.roles || [];
    const regions = payload.regions || [];

    const hasSearched = Boolean(executedQuery);

    const filteredUsers = useMemo(() => {
        const term = executedQuery.toLowerCase();
        if (!term) return [];
        return users.filter((user) =>
            [user.name, user.email, user.region, user.role]
                .filter(Boolean)
                .some((value) => value.toLowerCase().includes(term))
        );
    }, [users, executedQuery]);

    const runSearch = () => {
        setExecutedQuery(searchInput.trim());
    };

    const addUser = useMutation({
        mutationFn: async (body) =>
            axiosInstance.post("/operationmonitor/api/v1/admin/users", body),
        onSuccess: () => {
            enqueueSnackbar("User added", { variant: "success" });
            setAddOpen(false);
            setForm(emptyForm);
            queryClient.invalidateQueries({ queryKey: ["admin-users"] });
            queryClient.invalidateQueries({ queryKey: ["admin-approvers"] });
        },
        onError: (error) => {
            enqueueSnackbar(
                error?.response?.data?.message || "Failed to add user",
                { variant: "error" }
            );
        },
    });

    const handleAddSubmit = () => {
        const emailTrimmed = form.email.trim();

        if (!ZOOM_EMAIL_REGEX.test(emailTrimmed)) {
            enqueueSnackbar("Please enter a valid email ending in @zoom.us or @zoom.com.", {
                variant: "error",
            });
            return;
        }
        const payloadBody = {
            name: form.name.trim(),
            email: emailTrimmed,
            ...(form.userType === "role"
                ? { role: form.role }
                : { region: form.region }),
        };
        addUser.mutate(payloadBody);
    };

    const changeRole = useMutation({
        mutationFn: async (body) =>
            axiosInstance.put("/operationmonitor/api/v1/admin/users", body),
        onSuccess: () => {
            enqueueSnackbar("Role updated", { variant: "success" });
            setPendingRoleChange(null);
            queryClient.invalidateQueries({ queryKey: ["admin-users"] });
            queryClient.invalidateQueries({ queryKey: ["admin-approvers"] });
        },
        onError: (error) => {
            enqueueSnackbar(
                error?.response?.data?.message || "Failed to change role",
                { variant: "error" }
            );
        },
    });

    const columns = [
        {
            field: "name",
            headerName: "Name",
            flex: 1,
            minWidth: 150,
            align: "left",
            headerAlign: "left",
            sortable: false,
            filterable: false,
        },
        {
            field: "email",
            headerName: "Email",
            flex: 1.2,
            minWidth: 200,
            align: "left",
            headerAlign: "left",
            sortable: false,
            filterable: false,
        },
        {
            field: "region",
            headerName: "Region",
            flex: 0.8,
            minWidth: 120,
            align: "left",
            headerAlign: "left",
            sortable: false,
            filterable: false,
            renderCell: (params) => params.row.region || "-",
        },
        {
            field: "tabs",
            headerName: "Tabs",
            flex: 1.5,
            minWidth: 260,
            align: "left",
            headerAlign: "left",
            sortable: false,
            filterable: false,
            renderCell: (params) => {
                const userTabs = params.row.tabs || [];
                if (!userTabs.length) {
                    return (
                        <Box sx={{ display: "flex", alignItems: "center", height: "100%", width: "100%", justifyContent: "flex-start" }}>
                            <Typography variant="body2" color="text.secondary">-</Typography>
                        </Box>
                    );
                }
                return (
                    <Stack
                        direction="row"
                        spacing={0.5}
                        sx={{
                            flexWrap: "wrap",
                            gap: 0.5,
                            alignItems: "center",
                            justifyContent: "flex-start",
                            width: "100%",
                            py: 1,
                        }}
                    >
                        {userTabs.map((tab) => (
                            <Chip
                                key={tab}
                                label={tab}
                                size="small"
                                variant="outlined"
                                sx={{ fontSize: "11px", height: "22px", borderRadius: "4px" }}
                            />
                        ))}
                    </Stack>
                );
            },
        },
        {
            field: "role",
            headerName: "Role",
            flex: 1,
            minWidth: 180,
            align: "left",
            headerAlign: "left",
            sortable: false,
            filterable: false,
            renderCell: (params) => {
                const isAdminRow = (params.row.role || "").toLowerCase() === "admin";
                return (
                    <Autocomplete
                        size="small"
                        disabled={isAdminRow || changeRole.isPending}
                        options={roles}
                        value={params.row.role || null}
                        onChange={(_event, value) => {
                            if (!value || value === params.row.role) return;
                            setPendingRoleChange({
                                email: params.row.email,
                                name: params.row.name,
                                fromRole: params.row.role,
                                toRole: value,
                            });
                        }}
                        renderInput={(inputParams) => (
                            <TextField
                                {...inputParams}
                                variant="outlined"
                                size="small"
                                placeholder={params.row.region ? "Region User" : "Select Role"}
                                sx={{ minWidth: 160 }}
                            />
                        )}
                        sx={{ width: "100%", marginTop: "6px" }}
                    />
                );
            },
        },
    ];

    const canSubmitAdd =
        form.name.trim() &&
        form.email.trim() &&
        (form.userType === "role" ? Boolean(form.role) : Boolean(form.region)) &&
        !addUser.isPending;

    const closeAdd = () => {
        if (addUser.isPending) return;
        setAddOpen(false);
        setForm(emptyForm);
    };

    return (
        <Box>
            <Box
                sx={{
                    display: "grid",
                    gridTemplateColumns: {
                        xs: "1fr",
                        md: "minmax(140px, auto) minmax(240px, 1fr) auto auto",
                    },
                    alignItems: "center",
                    gap: 1.5,
                    marginBottom: hasSearched ? 2 : 0,
                }}
            >
                <Typography
                    variant="h6"
                    className="operations-container-title operations-3d-title"
                    sx={{ margin: 0, lineHeight: 1.3 }}
                >
                    User roles
                </Typography>
                <TextField
                    placeholder="Search by name, email, region, or role"
                    value={searchInput}
                    onChange={(event) => setSearchInput(event.target.value)}
                    onKeyDown={(event) => {
                        if (event.key === "Enter") {
                            event.preventDefault();
                            runSearch();
                        }
                    }}
                    size="small"
                    InputProps={{
                        startAdornment: (
                            <InputAdornment position="start">
                                <SearchIcon fontSize="small" />
                            </InputAdornment>
                        ),
                    }}
                />
                <Button type="primary" onClick={runSearch}>
                    Search
                </Button>
                <Button type="primary" onClick={() => setAddOpen(true)}>
                    Add New User
                </Button>
            </Box>

            {hasSearched && (
                <div className="datagrid-parent-container">
                    <DataGrid
                        rows={filteredUsers}
                        columns={columns}
                        getRowId={(row) => row.email}
                        columnHeaderHeight={70}
                        rowHeight={64}
                        pageSizeOptions={[10, 20, 50]}
                        initialState={{
                            pagination: { paginationModel: { pageSize: 10 } },
                        }}
                        disableRowSelectionOnClick
                        loading={isFetching}
                        slots={{ toolbar: CustomDataGridToolBar }}
                        localeText={{ noRowsLabel: "No users match this search" }}
                        sx={{
                            width: "100%",
                            height: 420,
                            "& .MuiDataGrid-columnHeaders": {
                                backgroundColor: "#f6f6f6",
                                fontWeight: "bold",
                            },
                            "& .MuiDataGrid-columnHeader": {
                                justifyContent: "flex-start !important",
                                textAlign: "left !important",
                            },
                            "& .MuiDataGrid-columnHeaderTitle": {
                                fontWeight: "bold",
                                textAlign: "left !important",
                            },
                            "& .MuiDataGrid-cell": {
                                justifyContent: "flex-start !important",
                                display: "flex",
                                alignItems: "center",
                            },
                        }}
                    />
                </div>
            )}

            <Dialog
                open={Boolean(pendingRoleChange)}
                onClose={() => !changeRole.isPending && setPendingRoleChange(null)}
                maxWidth="sm"
                fullWidth
            >
                <DialogTitle
                    sx={{
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "space-between",
                        pr: 1,
                        paddingBottom: 1,
                    }}
                >
                    Confirm role change
                    <IconButton
                        aria-label="Close"
                        onClick={() => setPendingRoleChange(null)}
                        disabled={changeRole.isPending}
                        size="small"
                        sx={{ color: "text.secondary" }}
                    >
                        <CloseIcon />
                    </IconButton>
                </DialogTitle>
                <DialogContent>
                    <Typography>
                        Change {pendingRoleChange?.name || pendingRoleChange?.email} from{" "}
                        {pendingRoleChange?.fromRole} to {pendingRoleChange?.toRole}?
                    </Typography>
                </DialogContent>
                <DialogActions sx={{ padding: "8px 24px 20px", gap: 1 }}>
                    <Button
                        disabled={changeRole.isPending}
                        onClick={() => setPendingRoleChange(null)}
                    >
                        Cancel
                    </Button>
                    <Button
                        type="primary"
                        disabled={changeRole.isPending}
                        onClick={() =>
                            changeRole.mutate({
                                email: pendingRoleChange.email,
                                role: pendingRoleChange.toRole,
                            })
                        }
                    >
                        Confirm
                    </Button>
                </DialogActions>
            </Dialog>

            <Dialog
                open={addOpen}
                onClose={closeAdd}
                maxWidth="sm"
                fullWidth
            >
                <DialogTitle
                    sx={{
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "space-between",
                        pr: 1,
                    }}
                >
                    Add New User
                    <IconButton
                        aria-label="Close"
                        onClick={closeAdd}
                        disabled={addUser.isPending}
                        size="small"
                        sx={{ color: "text.secondary" }}
                    >
                        <CloseIcon />
                    </IconButton>
                </DialogTitle>
                <DialogContent sx={{ display: "grid", gap: 2, paddingTop: "12px" }}>
                    <FormControl component="fieldset">
                        <RadioGroup
                            row
                            value={form.userType}
                            onChange={(e) =>
                                setForm((current) => ({
                                    ...current,
                                    userType: e.target.value,
                                }))
                            }
                        >
                            <FormControlLabel
                                value="role"
                                control={<Radio size="small" />}
                                label="Role Based (Default)"
                            />
                            <FormControlLabel
                                value="region"
                                control={<Radio size="small" />}
                                label="Region Based"
                            />
                        </RadioGroup>
                    </FormControl>
                    <TextField
                        label="Name"
                        required
                        value={form.name}
                        onChange={(event) =>
                            setForm((current) => ({ ...current, name: event.target.value }))
                        }
                        size="small"
                        autoFocus
                        sx={{ marginTop: 1 }}
                    />
                    <TextField
                        label="Email"
                        required
                        value={form.email}
                        onChange={(event) =>
                            setForm((current) => ({ ...current, email: event.target.value }))
                        }
                        size="small"
                    />
                    {form.userType === "role" ? (
                        <Autocomplete
                            options={roles}
                            value={form.role || null}
                            onChange={(_event, value) =>
                                setForm((current) => ({ ...current, role: value || "" }))
                            }
                            renderInput={(params) => (
                                <TextField {...params} label="Role" required size="small" />
                            )}
                        />
                    ) : (
                        <Autocomplete
                            options={regions}
                            value={form.region || null}
                            onChange={(_event, value) =>
                                setForm((current) => ({ ...current, region: value || "" }))
                            }
                            renderInput={(params) => (
                                <TextField {...params} label="Region" required size="small" />
                            )}
                        />
                    )}
                </DialogContent>
                <DialogActions sx={{ padding: "8px 24px 20px", gap: 1 }}>
                    <Button disabled={addUser.isPending} onClick={closeAdd}>
                        Cancel
                    </Button>
                    <Button
                        type="primary"
                        disabled={!canSubmitAdd}
                        onClick={handleAddSubmit}
                    >
                        Add
                    </Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
};

export default AdminUsers;